// Castle layout data, shared by the sim (collision, elevation levels, AI
// posts, waypoints, royal retreat routes) and the renderer (stone walls,
// stairs, crenellations, towers, roofs). Defined for the enemy castle
// (positive X) and mirrored for the friendly one.
//
// The castle is a real curtain-walled bailey now:
//
//   - Curtain wall x 162..238, z -58..58, with a walkable wall-walk on top
//     (height 7) behind a crenellated parapet.
//   - Main gate in the west wall (z -4..4) flanked by two gatehouse towers
//     with walkable tops; a postern side-door in the north wall (x 190..193).
//   - West corner towers with walkable crenellated tops; east corner towers
//     wear conical roofs (solid, decorative).
//   - Courtyard staircases up to the wall-walk beside the gate and the east
//     wall; short stair ramps on the wall-walk climb to the tower tops.
//   - The keep (x 196..234, z -32..32) has a walkable roof reached by an
//     external stone stair on its north face, and interior rooms: a west
//     corridor, the great hall (throne at x 228), north and south chambers,
//     and a cellar hidden at the corridor's south end.
//
// Royal retreat chain: throne → north chamber → south chamber → cellar.

import type { Team } from './types';
import type { ProfileId } from './profiles';
import type { Archetype } from './types';

export interface WallBox {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Top of the box (absolute). */
  height: number;
  /** Bottom of the box (absolute); default 0. Gate lintels float above doors. */
  minY?: number;
  /** The box top is a standable platform (wall-walks, tower tops, the roof). */
  walkable?: boolean;
  /** Renderer draws merlons along the top (battlement parapets). */
  crenellated?: boolean;
  /** Renderer hooks: 'roof' = the keep roof slab (hidden when inside). */
  tag?: 'roof';
}

/** A walkable stair/ramp: height interpolates h0 (at the axis-min end) to h1
 * (at the axis-max end). Rendered as stone steps; blocks passage beneath. */
export interface Ramp {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  axis: 'x' | 'z';
  h0: number;
  h1: number;
}

export function castleSign(team: Team): number {
  return team === 'friendly' ? -1 : 1;
}

function mirrorBox(b: WallBox, sign: number): WallBox {
  if (sign === 1) return b;
  return { ...b, minX: -b.maxX, maxX: -b.minX };
}

function mirrorRamp(r: Ramp, sign: number): Ramp {
  if (sign === 1) return r;
  const m = { ...r, minX: -r.maxX, maxX: -r.minX };
  // mirroring X flips which end of an X-axis ramp is the min end
  if (r.axis === 'x') {
    m.h0 = r.h1;
    m.h1 = r.h0;
  }
  return m;
}

export const CURTAIN_H = 7;
const PARAPET_H = 8.8;
const GATE_TOWER_H = 10;
const CORNER_TOWER_H = 11;
export const KEEP_H = 9;
const INNER_H = 5;

/** Keep footprint (roof rendering, "am I indoors" checks). */
export const KEEP_BOUNDS = { minX: 196, maxX: 234, minZ: -32, maxZ: 32, height: KEEP_H };

/** Walls for the +X castle; mirrored for the friendly side. */
const BASE_WALLS: WallBox[] = [
  // ── curtain wall (walkable wall-walk on top) ──
  // west wall with the main gate at z -4..4
  { minX: 162, maxX: 164.5, minZ: -58, maxZ: -4, height: CURTAIN_H, walkable: true },
  { minX: 162, maxX: 164.5, minZ: 4, maxZ: 58, height: CURTAIN_H, walkable: true },
  // gate lintel: the wall-walk continues over the gate arch
  { minX: 162, maxX: 164.5, minZ: -4, maxZ: 4, height: CURTAIN_H, minY: 4.5, walkable: true },
  // east wall
  { minX: 235.5, maxX: 238, minZ: -58, maxZ: 58, height: CURTAIN_H, walkable: true },
  // north wall with the postern side-door at x 190..193
  { minX: 162, maxX: 190, minZ: 55.5, maxZ: 58, height: CURTAIN_H, walkable: true },
  { minX: 193, maxX: 238, minZ: 55.5, maxZ: 58, height: CURTAIN_H, walkable: true },
  { minX: 190, maxX: 193, minZ: 55.5, maxZ: 58, height: CURTAIN_H, minY: 4.5, walkable: true },
  // south wall
  { minX: 162, maxX: 238, minZ: -58, maxZ: -55.5, height: CURTAIN_H, walkable: true },

  // ── battlement parapets (outer lip of each wall-walk) ──
  { minX: 162, maxX: 162.55, minZ: -58, maxZ: 58, height: PARAPET_H, minY: CURTAIN_H, crenellated: true },
  { minX: 237.45, maxX: 238, minZ: -58, maxZ: 58, height: PARAPET_H, minY: CURTAIN_H, crenellated: true },
  { minX: 162, maxX: 238, minZ: 57.45, maxZ: 58, height: PARAPET_H, minY: CURTAIN_H, crenellated: true },
  { minX: 162, maxX: 238, minZ: -58, maxZ: -57.45, height: PARAPET_H, minY: CURTAIN_H, crenellated: true },

  // ── gatehouse towers flanking the gate (walkable tops) ──
  { minX: 160, maxX: 168, minZ: 4, maxZ: 11, height: GATE_TOWER_H, walkable: true },
  { minX: 160, maxX: 168, minZ: -11, maxZ: -4, height: GATE_TOWER_H, walkable: true },
  ...towerParapet(160, 168, 4, 11, GATE_TOWER_H),
  ...towerParapet(160, 168, -11, -4, GATE_TOWER_H),

  // ── west corner towers (walkable crenellated tops) ──
  { minX: 158, maxX: 167, minZ: 54, maxZ: 63, height: CORNER_TOWER_H, walkable: true },
  { minX: 158, maxX: 167, minZ: -63, maxZ: -54, height: CORNER_TOWER_H, walkable: true },
  ...towerParapet(158, 167, 54, 63, CORNER_TOWER_H),
  ...towerParapet(158, 167, -63, -54, CORNER_TOWER_H),

  // ── east corner towers (solid, conical roofs — see roofedTowers) ──
  { minX: 231, maxX: 240, minZ: 54, maxZ: 63, height: 12 },
  { minX: 231, maxX: 240, minZ: -63, maxZ: -54, height: 12 },

  // ── the keep ──
  // west wall with the main door at z -3..3
  { minX: 196, maxX: 197.5, minZ: -32, maxZ: -3, height: KEEP_H },
  { minX: 196, maxX: 197.5, minZ: 3, maxZ: 32, height: KEEP_H },
  { minX: 196, maxX: 197.5, minZ: -3, maxZ: 3, height: KEEP_H, minY: 4.5 },
  // east, north, south (postern gap x 214..217)
  { minX: 232.5, maxX: 234, minZ: -32, maxZ: 32, height: KEEP_H },
  { minX: 196, maxX: 234, minZ: 30.5, maxZ: 32, height: KEEP_H },
  { minX: 196, maxX: 214, minZ: -32, maxZ: -30.5, height: KEEP_H },
  { minX: 217, maxX: 234, minZ: -32, maxZ: -30.5, height: KEEP_H },
  // walkable roof slab
  { minX: 196, maxX: 234, minZ: -32, maxZ: 32, height: KEEP_H, minY: 8.6, walkable: true, tag: 'roof' },
  // roof battlements (gap x 202..208 on the north edge for the stair)
  { minX: 196, maxX: 196.55, minZ: -32, maxZ: 32, height: 10.6, minY: KEEP_H, crenellated: true },
  { minX: 233.45, maxX: 234, minZ: -32, maxZ: 32, height: 10.6, minY: KEEP_H, crenellated: true },
  { minX: 196, maxX: 234, minZ: -32, maxZ: -31.45, height: 10.6, minY: KEEP_H, crenellated: true },
  { minX: 196, maxX: 202, minZ: 31.45, maxZ: 32, height: 10.6, minY: KEEP_H, crenellated: true },
  { minX: 208, maxX: 234, minZ: 31.45, maxZ: 32, height: 10.6, minY: KEEP_H, crenellated: true },

  // ── keep interior (corridor along the west side, hall, two chambers) ──
  // corridor divider x 203..204; doors: hall z -2..2, chambers z ±18..21
  { minX: 203, maxX: 204, minZ: -32, maxZ: -21, height: INNER_H },
  { minX: 203, maxX: 204, minZ: -18, maxZ: -2, height: INNER_H },
  { minX: 203, maxX: 204, minZ: 2, maxZ: 18, height: INNER_H },
  { minX: 203, maxX: 204, minZ: 21, maxZ: 32, height: INNER_H },
  // hall/chamber dividers with doors at x 216..220
  { minX: 204, maxX: 216, minZ: 13.5, maxZ: 15, height: INNER_H },
  { minX: 220, maxX: 232.5, minZ: 13.5, maxZ: 15, height: INNER_H },
  { minX: 204, maxX: 216, minZ: -15, maxZ: -13.5, height: INNER_H },
  { minX: 220, maxX: 232.5, minZ: -15, maxZ: -13.5, height: INNER_H },
  // cellar at the corridor's south end (entry gap x 201..203)
  { minX: 197.5, maxX: 201, minZ: -26, maxZ: -25, height: INNER_H },

  // great-hall columns (two rows flanking the carpet to the throne)
  ...[208, 214, 220, 226].flatMap((x) =>
    [8, -8].map((z) => ({
      minX: x - 0.45,
      maxX: x + 0.45,
      minZ: z - 0.45,
      maxZ: z + 0.45,
      height: 8.6,
    }))
  ),
];

/** Torch positions inside the keep (renderer builds sconces + point lights).
 * y is the sconce height. */
export function interiorTorches(team: Team): { x: number; y: number; z: number }[] {
  const s = castleSign(team);
  const list = [
    // great hall: sconces mounted on the column faces along the carpet
    { x: 208, y: 3.4, z: 7.3 },
    { x: 208, y: 3.4, z: -7.3 },
    { x: 214, y: 3.4, z: 7.3 },
    { x: 214, y: 3.4, z: -7.3 },
    { x: 220, y: 3.4, z: 7.3 },
    { x: 220, y: 3.4, z: -7.3 },
    { x: 226, y: 3.4, z: 7.3 },
    { x: 226, y: 3.4, z: -7.3 },
    // corridor (on the divider wall)
    { x: 203.2, y: 3.2, z: 10 },
    { x: 203.2, y: 3.2, z: -10 },
    // chambers (on the east wall)
    { x: 232.2, y: 3.2, z: 24 },
    { x: 232.2, y: 3.2, z: -24 },
    // cellar
    { x: 199, y: 2.6, z: -28.5 },
  ];
  return list.map((t) => ({ x: s * t.x, y: t.y, z: t.z }));
}

/** Four thin crenellated boxes ringing a tower top. */
function towerParapet(
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number,
  base: number
): WallBox[] {
  const h = base + 1.6;
  const t = 0.55;
  return [
    { minX, maxX: minX + t, minZ, maxZ, height: h, minY: base, crenellated: true },
    { minX: maxX - t, maxX, minZ, maxZ, height: h, minY: base, crenellated: true },
    { minX: minX + t, maxX: maxX - t, minZ, maxZ: minZ + t, height: h, minY: base, crenellated: true },
    { minX: minX + t, maxX: maxX - t, minZ: maxZ - t, maxZ, height: h, minY: base, crenellated: true },
  ];
}

/** Stairs for the +X castle; mirrored for the friendly side. */
const BASE_RAMPS: Ramp[] = [
  // courtyard staircases up to the west wall-walk, flanking the gate
  { minX: 164.5, maxX: 172.5, minZ: 13, maxZ: 16, axis: 'x', h0: CURTAIN_H, h1: 0 },
  { minX: 164.5, maxX: 172.5, minZ: -16, maxZ: -13, axis: 'x', h0: CURTAIN_H, h1: 0 },
  // courtyard staircases up to the east wall-walk, behind the keep
  { minX: 227.5, maxX: 235.5, minZ: 40, maxZ: 43, axis: 'x', h0: 0, h1: CURTAIN_H },
  { minX: 227.5, maxX: 235.5, minZ: -43, maxZ: -40, axis: 'x', h0: 0, h1: CURTAIN_H },
  // wall-walk ramps up to the gatehouse tower tops
  { minX: 162, maxX: 164.5, minZ: 11, maxZ: 15, axis: 'z', h0: GATE_TOWER_H, h1: CURTAIN_H },
  { minX: 162, maxX: 164.5, minZ: -15, maxZ: -11, axis: 'z', h0: CURTAIN_H, h1: GATE_TOWER_H },
  // north/south wall-walk ramps up to the west corner tower tops
  { minX: 167, maxX: 172, minZ: 55.5, maxZ: 58, axis: 'x', h0: CORNER_TOWER_H, h1: CURTAIN_H },
  { minX: 167, maxX: 172, minZ: -58, maxZ: -55.5, axis: 'x', h0: CORNER_TOWER_H, h1: CURTAIN_H },
  // external stone stair up the keep's north face to the roof
  { minX: 204, maxX: 222, minZ: 32, maxZ: 35, axis: 'x', h0: KEEP_H, h1: 0 },
];

/** Decorative conical roofs (the east corner towers). */
export interface RoofedTower {
  x: number;
  z: number;
  radius: number;
  base: number;
}

const BASE_ROOFED_TOWERS: RoofedTower[] = [
  { x: 235.5, z: 58.5, radius: 5.6, base: 12 },
  { x: 235.5, z: -58.5, radius: 5.6, base: 12 },
];

export function castleWalls(team: Team): WallBox[] {
  const sign = castleSign(team);
  return BASE_WALLS.map((b) => mirrorBox(b, sign));
}

export function castleRamps(team: Team): Ramp[] {
  const sign = castleSign(team);
  return BASE_RAMPS.map((r) => mirrorRamp(r, sign));
}

export function roofedTowers(team: Team): RoofedTower[] {
  const sign = castleSign(team);
  return BASE_ROOFED_TOWERS.map((t) => ({ ...t, x: sign * t.x }));
}

export function keepBounds(team: Team): WallBox {
  return mirrorBox({ ...KEEP_BOUNDS }, castleSign(team));
}

export function thronePosition(team: Team): { x: number; z: number } {
  return { x: castleSign(team) * 228, z: 0 };
}

/** Muster point outside the gate where bought troops arrive. */
export function musterPosition(team: Team): { x: number; z: number } {
  return { x: castleSign(team) * 152, z: 0 };
}

/** Retreat chain for the King: throne, chambers, then the cellar. */
export function royalChambers(team: Team): { x: number; z: number }[] {
  const s = castleSign(team);
  return [
    { x: s * 228, z: 0 },
    { x: s * 226, z: 22 },
    { x: s * 226, z: -22 },
    { x: s * 200, z: -29 },
  ];
}

/** Waypoint route to reach chamber `idx` (door-gap safe from the previous
 * chamber in the chain). */
export function chamberRoute(team: Team, idx: number): { x: number; z: number }[] {
  const s = castleSign(team);
  if (idx === 1) {
    // throne → hall north door (x 216..220, z 13.5..15) → north chamber
    return [
      { x: s * 218, z: 6 },
      { x: s * 218, z: 17 },
      { x: s * 226, z: 22 },
    ];
  }
  if (idx === 2) {
    return [
      { x: s * 218, z: -6 },
      { x: s * 218, z: -17 },
      { x: s * 226, z: -22 },
    ];
  }
  if (idx === 3) {
    // south chamber → corridor door (z -21..-18) → down the corridor → cellar
    return [
      { x: s * 212, z: -19.5 },
      { x: s * 206, z: -19.5 },
      { x: s * 200, z: -19.5 },
      { x: s * 200, z: -24 },
      { x: s * 202, z: -25.5 },
      { x: s * 200, z: -29 },
    ];
  }
  return [{ x: s * 228, z: 0 }];
}

export type GuardRole = 'royal' | 'gate' | 'patrol' | 'wall' | 'sentry' | 'offduty' | 'archer';

export interface GuardPost {
  x: number;
  z: number;
  /** Spawn elevation (wall archers and wall patrols stand on the wall-walk). */
  elev: number;
  profile: ProfileId;
  maxHealth: number;
  archetype: Archetype;
  role: GuardRole;
  /** Patrol route id (see patrolRoute) for patrol/wall/sentry roles. */
  route?: string;
  /** Waypoint index to start the route from (staggers paired patrols). */
  routeStart?: number;
}

/** The castle garrison, with a job for everyone: royal guards in the hall,
 * gate watch, courtyard and wall patrols, a keep sentry walking the rooms,
 * two off-duty men by the well, and four archers on the battlements. */
export function guardPosts(team: Team): GuardPost[] {
  const s = castleSign(team);
  const post = (
    x: number,
    z: number,
    role: GuardRole,
    profile: ProfileId,
    hp: number,
    extra: Partial<GuardPost> = {}
  ): GuardPost => ({
    x: s * x,
    z,
    elev: 0,
    profile,
    maxHealth: hp,
    archetype: 'infantry',
    role,
    ...extra,
  });
  const wallArcher = (z: number): GuardPost => ({
    x: s * 163.2,
    z,
    elev: CURTAIN_H,
    profile: 'patrolGuard',
    maxHealth: 90,
    archetype: 'archer',
    role: 'archer',
  });
  return [
    post(229, 4, 'royal', 'royalGuard', 150),
    post(229, -4, 'royal', 'royalGuard', 150),
    post(158.5, 6, 'gate', 'patrolGuard', 110),
    post(158.5, -6, 'gate', 'patrolGuard', 110),
    post(170, 10, 'patrol', 'patrolGuard', 110, { route: 'courtyard', routeStart: 0 }),
    post(193, -10, 'patrol', 'patrolGuard', 110, { route: 'courtyard', routeStart: 3 }),
    post(174, 56.7, 'wall', 'patrolGuard', 100, { route: 'northwall', elev: CURTAIN_H }),
    post(199.5, 0, 'sentry', 'patrolGuard', 110, { route: 'keep' }),
    post(176.5, 22.5, 'offduty', 'patrolGuard', 100),
    post(180, 25.5, 'offduty', 'patrolGuard', 100),
    wallArcher(22),
    wallArcher(-22),
    wallArcher(38),
    wallArcher(-38),
  ];
}

/** Patrol loops (consecutive waypoints have straight walkable lines). */
export function patrolRoute(team: Team, id: string): { x: number; z: number }[] {
  const s = castleSign(team);
  let route: { x: number; z: number }[];
  if (id === 'northwall') {
    // pace the north wall-walk (between the corner-tower ramp and the rear
    // stairs so the patrol never strays onto a tower)
    route = [
      { x: 174, z: 56.7 },
      { x: 226, z: 56.7 },
    ];
  } else if (id === 'keep') {
    // through the hall, past the throne, then the length of the corridor
    route = [
      { x: 212, z: 0 },
      { x: 226, z: 5 },
      { x: 212, z: 0 },
      { x: 199.5, z: 0 },
      { x: 199.5, z: 16 },
      { x: 199.5, z: -16 },
      { x: 199.5, z: 0 },
    ];
  } else {
    // courtyard circuit: gate, well, keep door, south side
    route = [
      { x: 170, z: 10 },
      { x: 178, z: 22 },
      { x: 191, z: 12 },
      { x: 193, z: -10 },
      { x: 178, z: -22 },
      { x: 167, z: -10 },
    ];
  }
  return route.map((p) => ({ x: s * p.x, z: p.z }));
}

/** Where the off-duty pair takes up arms when the alarm sounds. */
export function alarmPosts(team: Team): { x: number; z: number }[] {
  const s = castleSign(team);
  return [
    { x: s * 167.5, z: 4 },
    { x: s * 167.5, z: -4 },
  ];
}

/** Extra bodyguards from the tech tree flank the throne. */
export function bodyguardPosts(team: Team): { x: number; z: number }[] {
  const s = castleSign(team);
  return [
    { x: s * 230.5, z: 5 },
    { x: s * 230.5, z: -5 },
  ];
}

/** Route an assassin of `team` takes toward the OPPOSING castle. */
export function assassinWaypoints(team: Team): { x: number; z: number }[] {
  const t = -castleSign(team); // sign of the target castle
  return [
    { x: -t * 156, z: 0 }, // out through our own gate first
    { x: -t * 100, z: -80 },
    { x: 0, z: -108 },
    { x: t * 110, z: -80 },
    { x: t * 140, z: -30 },
    { x: t * 152, z: -6 },
    { x: t * 158, z: 0 },
    { x: t * 168, z: 0 }, // through the gate
    { x: t * 185, z: 0 }, // courtyard
    { x: t * 194, z: 0 }, // keep door
    { x: t * 200, z: 0 }, // corridor
    { x: t * 210, z: 0 }, // through the hall door
    { x: t * 228, z: 0 }, // the throne
  ];
}
